import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Import the new hook and other dependencies
import { useChatScroll } from "../../hooks/useChatScroll";
import { ChatMessage } from "./ChatMessage";
import {
  CvEditorSession,
  CvEditorSessionStatus,
  CvMessage,
} from "../../types/cv-editor.types";

interface ChatHistoryProps {
  session: CvEditorSession;
  chatHistory: CvMessage[];
  isThinking: boolean;
}

const PositionHeader: React.FC<{ position: any }> = ({ position }) => {
  const { t } = useTranslation("cvEditor");
  if (!position) return null;

  return (
    <div className="p-4 my-2 bg-gray-100 border border-gray-300 rounded-lg shadow-md ">
      <div className="flex items-center gap-3 mb-2">
        <WorkHistoryIcon className="text-gray-400" />
        <h4 className="font-bold text-gray-800">
          {t("interview.position.conversationStarter")}
        </h4>
      </div>
      <div className="pl-8 text-sm space-y-1">
        <p>
          <span className="font-semibold text-gray-700">
            {t("positionsStep.positionLabel")}:
          </span>{" "}
          {position.position}
        </p>
        <p>
          <span className="font-semibold text-gray-700">
            {t("positionsStep.companyLabel")}:
          </span>{" "}
          {position.company}
        </p>
        <p>
          <span className="font-semibold text-gray-700">
            {t("positionsStep.dateLabel-simplified")}:
          </span>{" "}
          {position.date}
        </p>
        {position.summary && (
          <p className="text-gray-600 pt-1 italic">"{position.summary}"</p>
        )}
      </div>
    </div>
  );
};

export const ChatHistory: React.FC<ChatHistoryProps> = ({
  session,
  chatHistory,
  isThinking,
}) => {
  const { t } = useTranslation("cvEditor");

  const chatEndRef = useChatScroll([chatHistory, isThinking]);

  const interleavedContent = useMemo(() => {
    const content: (
      | CvMessage
      | { type: "header"; id: string; positionIndex: number }
    )[] = [...chatHistory];
    const processedIndexes = new Set<number>();

    // Find the first message for each position index and insert a header before it.
    chatHistory.forEach((msg, i) => {
      if (
        msg.positionIndex !== undefined &&
        !processedIndexes.has(msg.positionIndex)
      ) {
        // Find the index of the first message for this position in the main 'content' array
        const insertAtIndex = content.findIndex(
          (c) => (c as CvMessage).positionIndex === msg.positionIndex
        );

        if (insertAtIndex !== -1) {
          content.splice(insertAtIndex, 0, {
            type: "header",
            id: `header-${msg.positionIndex}`,
            positionIndex: msg.positionIndex,
          });
        }
        processedIndexes.add(msg.positionIndex);
      }
    });

    // Handle the "About Me" starter message if that interview has begun
    if (
      session.status === CvEditorSessionStatus.INTERVIEW_ABOUT_ME_IN_PROGRESS &&
      chatHistory.some((msg) => msg.positionIndex === undefined)
    ) {
      const insertAtIndex = content.findIndex(
        (c) => (c as CvMessage).positionIndex === undefined
      );
      if (insertAtIndex !== -1) {
        content.splice(insertAtIndex, 0, {
          type: "header",
          id: "header-aboutme",
          positionIndex: -1, // Special index for about me
        });
      }
    }

    return content;
  }, [chatHistory, session.status]);

  return (
    <div className="flex-grow overflow-y-auto p-4 space-y-2 bg-gray-50">
      {/* No longer need renderConversationStarter */}

      {interleavedContent.map((item: any) => {
        if (item.type === "header") {
          if (item.positionIndex === -1) {
            // About Me header
            return (
              <div key={item.id} className="flex justify-center my-4">
                <div className="flex items-center gap-2 px-4 py-2 text-xs text-gray-600 bg-white border border-gray-200 rounded-full shadow-sm">
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  <span>{t("interview.aboutMe.conversationStarter")}</span>
                </div>
              </div>
            );
          }
          // Position header
          const position =
            session.parsedCvData?.sections?.experience?.items?.[
              item.positionIndex
            ];
          return <PositionHeader key={item.id} position={position} />;
        }
        // It's a regular chat message
        return <ChatMessage key={item.id} message={item} />;
      })}

      {isThinking && (
        <div className="flex justify-start">
          <div className="px-4 py-2 text-sm text-gray-500 bg-white rounded-2xl rounded-bl-none shadow-md animate-pulse">
            {t("interview.thinking")}
          </div>
        </div>
      )}

      <div ref={chatEndRef} />
    </div>
  );
};
